import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '../patterns';
import { FertilizerStockBarsRow } from './FertilizerStockBarsRow';
import { enrichFertilizerStockUsage, type EnrichedFertilizerStockUsageRow } from '../../utils/fertilizerStockUsageMath';
import type { FertilizerStockUsageRow } from '../../services/nutritionService';
import './FertilizerStockUsageWidget.css';

type Filter  = 'all' | 'atrisk' | 'variance';
type Density = 'comfortable' | 'compact';

export interface FertilizerStockUsageWidgetProps {
  rows:            FertilizerStockUsageRow[];
  lowStockPct?:    number;
  varianceTolPct?: number;
  initialFilter?:  Filter;
  initialDensity?: Density;
}

const MOBILE_BREAKPOINT = '(max-width: 767px)';

function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_BREAKPOINT).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(MOBILE_BREAKPOINT);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);
  return isMobile;
}

const matchesFilter = (r: EnrichedFertilizerStockUsageRow, f: Filter): boolean => {
  if (f === 'all') return true;
  if (f === 'atrisk') return r.stockStatus !== 'sufficient';
  if (f === 'variance') return r.varianceStatus !== 'ontarget' && r.cPlanned > 0;
  return true;
};

export const FertilizerStockUsageWidget: React.FC<FertilizerStockUsageWidgetProps> = ({
  rows,
  lowStockPct,
  varianceTolPct,
  initialFilter = 'all',
  initialDensity = 'comfortable',
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [filter, setFilter]   = useState<Filter>(initialFilter);
  const [density, setDensity] = useState<Density>(initialDensity);

  const enrichedRows = useMemo(
    () => enrichFertilizerStockUsage(rows, { lowPct: lowStockPct, tolPct: varianceTolPct }),
    [rows, lowStockPct, varianceTolPct],
  );

  const filteredRows = useMemo(
    () => enrichedRows.filter(r => matchesFilter(r, filter)),
    [enrichedRows, filter],
  );

  const counts = useMemo(() => ({
    all:      enrichedRows.length,
    atrisk:   enrichedRows.filter(r => matchesFilter(r, 'atrisk')).length,
    variance: enrichedRows.filter(r => matchesFilter(r, 'variance')).length,
  }), [enrichedRows]);

  if (rows.length === 0) {
    return (
      <section
        className="fsu-widget fsu-widget--empty"
        data-testid="fertilizer-stock-usage-widget"
      >
        <Banner
          tone="offline"
          title={t('nutrition.dashboard.fertilizerStockUsage.empty.title')}
          detail={t('nutrition.dashboard.fertilizerStockUsage.empty.detail')}
        />
      </section>
    );
  }

  const bodyClassName = ['fsu-rows', `density-${density}`, isMobile ? 'fsu-is-mobile' : null]
    .filter(Boolean).join(' ');

  return (
    <section
      className={`fsu-widget${isMobile ? ' fsu-is-mobile' : ''}`}
      aria-labelledby="fsu-title"
      data-testid="fertilizer-stock-usage-widget"
    >
      <header className="fsu-chrome">
        <div className="fsu-title-block">
          <div className="fsu-eyebrow" aria-hidden="true">
            {t('nutrition.dashboard.fertilizerStockUsage.eyebrow')}
          </div>
          <h2 id="fsu-title" className="fsu-title">
            {t('nutrition.dashboard.fertilizerStockUsage.title')}
          </h2>
          <p className="fsu-subtitle">
            {t('nutrition.dashboard.fertilizerStockUsage.subtitle')}
          </p>
        </div>

        <div className="fsu-chrome-controls">
          <div
            className="fsu-chips"
            role="radiogroup"
            aria-label={t('nutrition.dashboard.fertilizerStockUsage.aria.filters')}
          >
            {([
              { id: 'all',      label: t('nutrition.dashboard.fertilizerStockUsage.filters.all'),      tone: '' },
              { id: 'atrisk',   label: t('nutrition.dashboard.fertilizerStockUsage.filters.atrisk'),   tone: 'danger' },
              { id: 'variance', label: t('nutrition.dashboard.fertilizerStockUsage.filters.variance'), tone: 'warning' },
            ] as const).map(c => (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={filter === c.id}
                className={`fsu-chip ${filter === c.id ? 'is-on' : ''} ${c.tone}`}
                onClick={() => setFilter(c.id)}
              >
                {c.label}
                <span className="fsu-chip-count">{counts[c.id]}</span>
              </button>
            ))}
          </div>

          <div className="fsu-chrome-right">
            <span className="fsu-count">
              {t('nutrition.dashboard.fertilizerStockUsage.count', { filtered: filteredRows.length, total: enrichedRows.length })}
            </span>
            {!isMobile && (
              <div
                className="fsu-density"
                role="radiogroup"
                aria-label={t('nutrition.dashboard.fertilizerStockUsage.aria.density')}
              >
                {([
                  { id: 'comfortable', label: t('nutrition.dashboard.fertilizerStockUsage.density.comfortable') },
                  { id: 'compact',     label: t('nutrition.dashboard.fertilizerStockUsage.density.compact') },
                ] as const).map(d => (
                  <button
                    key={d.id}
                    type="button"
                    role="radio"
                    aria-checked={density === d.id}
                    className={`fsu-dchip ${density === d.id ? 'is-on' : ''}`}
                    onClick={() => setDensity(d.id)}
                  >{d.label}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="fsu-body">
        {filteredRows.length === 0 ? (
          <p className="fsu-filter-empty" role="status">
            {t('nutrition.dashboard.fertilizerStockUsage.empty.filteredOut')}
          </p>
        ) : (
          // list-style:none suppresses ul list semantics in Safari/VoiceOver; role="list" restores them
          // eslint-disable-next-line jsx-a11y/no-redundant-roles
          <ul role="list" className={bodyClassName}>
            {filteredRows.map(r => (
              <li
                key={r.productId}
                aria-label={t('nutrition.dashboard.fertilizerStockUsage.aria.row', {
                  product: r.productName,
                  stockStatus: r.stockStatus,
                  varianceStatus: r.varianceStatus,
                })}
              >
                <FertilizerStockBarsRow row={r} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

export default FertilizerStockUsageWidget;
